import { useMemo } from 'react'
import { useUserContext } from '../providers/UserContextProvider'
import { isWorkRole } from '../lib/membershipScope'
import type { MembershipEntityType, MembershipRole, OwnerType } from '../types'

const ROLE_ORDINAL: Record<MembershipRole, number> = {
  VIEWER: 0,
  CONTRIBUTOR: 1,
  MANAGER: 2,
  ADMIN: 3,
  OWNER: 4,
}

export interface Permissions {
  /** Can create/update domain data (objectives, KRs, BOs) */
  canCreate: boolean
  /** Can edit/update existing domain data */
  canEdit: boolean
  /** Can delete domain data */
  canDelete: boolean
  /** Can manage team members (add/remove/change roles) */
  canManageMembers: boolean
  /** Can approve/reject workflows */
  canApprove: boolean
  /** Can access system administration (settings, billing, users) */
  canAdmin: boolean
  /**
   * Can run a period close-out (score, carry forward, close). A period spans the
   * whole organization, so this is the admin track plus the work-track
   * organization leader — mirrors backend `perm.canManagePeriodLifecycle`.
   */
  canClosePeriod: boolean
  /** Is the workspace owner (can delete org, transfer ownership) */
  isOwner: boolean
  /** Is a read-only viewer with no write access */
  isViewer: boolean
  /** The user's primary membership role */
  role: MembershipRole | null
  /** Can create/update domain data owned by the provided entity */
  canEditTarget: (target: PermissionTarget) => boolean
  /** Can delete domain data owned by the provided entity */
  canDeleteTarget: (target: PermissionTarget) => boolean
  /** Is MANAGER at-or-above the provided entity — mirrors backend `canManageContent` */
  canManageTarget: (target: PermissionTarget) => boolean
}

interface PermissionTargetPath {
  organizationId?: string | null
  divisionId?: string | null
  valueStreamId?: string | null
  productTeamId?: string | null
  /**
   * Every unit above the target, at any level. A ladder with a level the four
   * fields cannot name (a product area) still has a chain, and a membership
   * anywhere on it counts -- the backend walks ancestors the same way.
   */
  ancestorIds?: string[]
}

export interface PermissionTarget {
  ownerType: OwnerType | MembershipEntityType | string
  ownerId: string
  ownerPath?: PermissionTargetPath
}

function roleMeets(role: MembershipRole, minRole: MembershipRole): boolean {
  return (ROLE_ORDINAL[role] ?? -1) >= ROLE_ORDINAL[minRole]
}

function toMembershipEntityType(ownerType: string): MembershipEntityType | null {
  if (
    ownerType === 'ORGANIZATION' ||
    ownerType === 'DIVISION' ||
    ownerType === 'VALUE_STREAM' ||
    ownerType === 'PRODUCT_TEAM' ||
    ownerType === 'UNIT'
  ) {
    return ownerType
  }
  return null
}

/**
 * Derives UI permissions from the user's primary membership role.
 * Use this to gate action buttons, quick actions, and admin features.
 */
export function usePermissions(): Permissions {
  const { primaryContext, hasOrgAdminAccess, isOrgAdmin, adminOrganizationId, activeMemberships } = useUserContext()

  return useMemo(() => {
    const role = primaryContext?.role as MembershipRole | null
    const ordinal = role ? (ROLE_ORDINAL[role] ?? 0) : 0
    const active = activeMemberships ?? []

    // Content permissions count work-track memberships, plus org_admin scoped
    // to their own org — mirrors backend `canReadContent`/`hasWorkPermission`.
    const hasScopedPermission = (target: PermissionTarget, minRole: MembershipRole): boolean => {
      const ownerType = toMembershipEntityType(target.ownerType)
      if (!ownerType || !target.ownerId) return false

      const targetOrgId =
        target.ownerPath?.organizationId ?? (ownerType === 'ORGANIZATION' ? target.ownerId : primaryContext?.organizationId)
      // Gated on the Keycloak org_admin role specifically (not the broader
      // hasOrgAdminAccess, which also covers a plain ADMIN/OWNER membership
      // whose role sync hasn't landed) — mirrors the backend's `isOrgAdmin(auth)`
      // gate around `hasScopedOrgAdminAccess`.
      if (isOrgAdmin && targetOrgId && targetOrgId === adminOrganizationId) return true

      return active.some((membership) => {
        if (membership.status !== 'ACTIVE' || !isWorkRole(membership.role) || !roleMeets(membership.role, minRole)) return false

        if (membership.entityType === ownerType && membership.entityId === target.ownerId) {
          return true
        }

        const path = target.ownerPath
        if (path?.ancestorIds?.includes(membership.entityId)) {
          return true
        }
        if (
          ownerType === 'PRODUCT_TEAM' &&
          membership.entityType === 'VALUE_STREAM' &&
          membership.entityId === path?.valueStreamId
        ) {
          return true
        }
        if (
          (ownerType === 'PRODUCT_TEAM' || ownerType === 'VALUE_STREAM') &&
          membership.entityType === 'DIVISION' &&
          membership.entityId === path?.divisionId
        ) {
          return true
        }
        if (
          ownerType !== 'ORGANIZATION' &&
          membership.entityType === 'ORGANIZATION' &&
          membership.entityId === (path?.organizationId ?? primaryContext?.organizationId)
        ) {
          return true
        }

        return false
      })
    }

    return {
      canCreate: ordinal >= ROLE_ORDINAL.CONTRIBUTOR,
      canEdit: ordinal >= ROLE_ORDINAL.CONTRIBUTOR,
      canDelete: ordinal >= ROLE_ORDINAL.MANAGER,
      canManageMembers: ordinal >= ROLE_ORDINAL.MANAGER,
      canApprove: ordinal >= ROLE_ORDINAL.MANAGER,
      canAdmin: hasOrgAdminAccess,
      canClosePeriod:
        hasOrgAdminAccess ||
        active.some(
          (m) =>
            m.status === 'ACTIVE' &&
            m.entityType === 'ORGANIZATION' &&
            roleMeets(m.role, 'MANAGER')
        ),
      // OWNER lives on the admin track, not in the primary work context
      isOwner: active.some((m) => m.status === 'ACTIVE' && m.role === 'OWNER'),
      isViewer: ordinal === ROLE_ORDINAL.VIEWER && role != null,
      role,
      canEditTarget: (target) => hasScopedPermission(target, 'CONTRIBUTOR'),
      canDeleteTarget: (target) => hasScopedPermission(target, 'MANAGER'),
      canManageTarget: (target) => hasScopedPermission(target, 'MANAGER'),
    }
  }, [activeMemberships, hasOrgAdminAccess, isOrgAdmin, adminOrganizationId, primaryContext?.organizationId, primaryContext?.role])
}
